import React, { useState, useRef, useEffect } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Bot, 
  Send, 
  Loader2, 
  Sparkles, 
  Navigation2, 
  TrendingUp, 
  Shield,
  User,
  RotateCcw,
  Mic,
  MicOff
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { QuickPrompts } from './QuickPrompts';
import { v4 as uuidv4 } from 'uuid';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  agent?: string;
  timestamp: Date;
}

interface UserContext {
  loadedScore?: number;
  careerStage?: string;
  strengths?: string[];
  growthAreas?: string[];
  completedTools?: string[];
}

const AI_AGENTS = {
  'loaded-bot': {
    name: 'LoadedBot',
    role: 'Comprehensive Career Guide',
    icon: Bot,
    color: 'from-primary to-secondary',
    description: 'Your friendly comprehensive career guide',
    expertise: ['Career Planning', 'Skill Development', 'Goal Setting', 'Professional Growth']
  },
  'career-navigator': {
    name: 'Career Navigator',
    role: 'Strategic Planning Expert',
    icon: Navigation2,
    color: 'from-blue-500 to-cyan-500',
    description: 'Strategic planning for your career trajectory',
    expertise: ['Strategic Planning', 'Industry Insights', 'Long-term Goals', 'Market Analysis']
  },
  'score-booster': {
    name: 'Score Booster',
    role: 'Loaded Score™ Optimization',
    icon: TrendingUp,
    color: 'from-green-500 to-emerald-500',
    description: 'Optimize your professional capabilities',
    expertise: ['Score Optimization', 'Skill Assessment', 'Performance Tracking', 'Benchmarking']
  },
  'future-proof-advisor': {
    name: 'Future Proof Advisor',
    role: 'Career Resilience Expert',
    icon: Shield,
    color: 'from-purple-500 to-pink-500',
    description: 'Prepare for industry disruption and change',
    expertise: ['Future Trends', 'Adaptability', 'Resilience', 'Continuous Learning']
  }
};

export const EnhancedAIWorkspace = () => {
  const session = useSession();
  const isMobile = useIsMobile();
  const [selectedAgent, setSelectedAgent] = useState<keyof typeof AI_AGENTS>('loaded-bot');
  const [conversationsByAgent, setConversationsByAgent] = useState<Record<string, Message[]>>({});
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userContext, setUserContext] = useState<UserContext>({});
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get current agent's messages
  const currentMessages = conversationsByAgent[selectedAgent] || [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentMessages]);

  // Load user context and chat history on mount
  useEffect(() => {
    if (session?.user) {
      loadUserContext();
      loadChatHistory();
    }
  }, [session]);

  // Load chat history when agent changes
  useEffect(() => {
    if (session?.user) {
      loadChatHistory();
    }
  }, [selectedAgent, session]);

  const loadUserContext = async () => {
    try {
      // For now, use mock data since the exact database schema may vary
      // This can be updated later when the database structure is confirmed
      setUserContext({
        loadedScore: 85,
        careerStage: 'Senior Professional',
        strengths: ['Communication', 'Strategic Thinking', 'Technical Leadership'],
        growthAreas: ['Executive Presence', 'Market Analysis'],
        completedTools: ['Assessment', 'Big Sheet', 'Rate Calculator']
      });
    } catch (error) {
      console.error('Error loading user context:', error);
      // Set default context if loading fails
      setUserContext({
        loadedScore: 0,
        careerStage: 'Getting Started',
        strengths: [],
        growthAreas: [],
        completedTools: []
      });
    }
  };

  const loadChatHistory = async () => {
    if (!session?.user) return;

    try {
      console.log('Loading chat history for agent:', selectedAgent);
      
      const { data: chatData, error: chatError } = await supabase
        .from('chat_history')
        .select('message_text, role, created_at, id')
        .eq('user_id', session.user.id)
        .eq('agent_type', selectedAgent)
        .order('created_at', { ascending: true });

      if (chatError) {
        console.error('Error fetching chat history:', chatError);
        return;
      }

      if (chatData) {
        const formattedMessages: Message[] = chatData.map(msg => ({
          id: msg.id || uuidv4(),
          role: msg.role === 'assistant' ? 'assistant' : 'user',
          content: msg.message_text,
          agent: selectedAgent,
          timestamp: new Date(msg.created_at || Date.now())
        }));
        
        setConversationsByAgent(prev => ({
          ...prev,
          [selectedAgent]: formattedMessages
        }));
      }
    } catch (error) {
      console.error('Error in loadChatHistory:', error);
    }
  };

  const saveChatMessage = async (message: Message) => {
    if (!session?.user) return;

    try {
      const { error } = await supabase
        .from('chat_history')
        .insert({
          user_id: session.user.id,
          agent_type: selectedAgent,
          message_text: message.content,
          role: message.role,
          created_at: message.timestamp.toISOString()
        });

      if (error) {
        console.error('Error saving chat message:', error);
      }
    } catch (error) {
      console.error('Error in saveChatMessage:', error);
    }
  };

  const sendMessage = async (message: string) => {
    if (!message.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message.trim(),
      timestamp: new Date()
    };

    setConversationsByAgent(prev => ({
      ...prev,
      [selectedAgent]: [...(prev[selectedAgent] || []), userMessage]
    }));
    
    // Save user message to database
    await saveChatMessage(userMessage);
    
    setInputMessage('');
    setIsLoading(true);

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      console.log('Sending message to AI assistant:', {
        message: message.trim(),
        agent: selectedAgent,
        userContext
      });

      const response = await fetch(`https://ahxfekyakvpbghwqjtqk.supabase.co/functions/v1/ai-assistant-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFoeGZla3lha3ZwYmdod3FqdHFrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc1MDAwMTQsImV4cCI6MjA1MzA3NjAxNH0.kOOdlBEKVykmQr1oMIJrcN7x9_GTujAEMjKWLKuALUM`,
        },
        body: JSON.stringify({
          message: message.trim(),
          agent: selectedAgent,
          userContext,
          conversationHistory: currentMessages.slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }))
        })
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        console.error('No response body available');
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '',
        agent: selectedAgent,
        timestamp: new Date()
      };

      setConversationsByAgent(prev => ({
        ...prev,
        [selectedAgent]: [...(prev[selectedAgent] || []), assistantMessage]
      }));
      console.log('Starting to read stream...');

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('Stream reading completed');
          // Save the completed assistant message to database
          if (assistantMessage.content.trim()) {
            await saveChatMessage(assistantMessage);
          }
          break;
        }

        const chunk = decoder.decode(value);
        console.log('Received chunk:', chunk.substring(0, 100) + '...');
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('Parsed data:', data);
              if (data.content) {
                assistantMessage.content += data.content;
                setConversationsByAgent(prev => ({
                  ...prev,
                  [selectedAgent]: prev[selectedAgent]?.map(m => 
                    m.id === assistantMessage.id ? { ...assistantMessage } : m
                  ) || []
                }));
              }
            } catch (parseError) {
              console.log('Parse error:', parseError, 'for line:', line);
            }
          }
        }
      }

    } catch (error: any) {
      console.error('Error sending message:', error);
      if (error.name !== 'AbortError') {
        toast.error('Failed to send message. Please try again.');
        // Don't remove messages if we got a partial response
        const hasPartialResponse = currentMessages.some(m => m.role === 'assistant' && m.content.length > 0);
        if (!hasPartialResponse) {
          setConversationsByAgent(prev => ({
            ...prev,
            [selectedAgent]: prev[selectedAgent]?.slice(0, -1) || []
          })); // Only remove user message if no response was received
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const clearConversation = () => {
    setConversationsByAgent(prev => ({
      ...prev,
      [selectedAgent]: []
    }));
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
  };

  const currentAgent = AI_AGENTS[selectedAgent];
  const IconComponent = currentAgent.icon;

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-background via-card-bg/30 to-background">
      {/* Agent Selection Bar */}
      <div className="p-3 sm:p-4 border-b border-border/20 bg-card-bg/50 backdrop-blur-sm">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                AI Workspace
              </h1>
              <p className="text-sm text-text-muted">Choose your AI assistant</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearConversation}
              className="gap-2 hover:bg-red-500/10 hover:text-red-500"
            >
              <RotateCcw className="w-4 h-4" />
              {!isMobile && 'Clear'}
            </Button>
          </div>

          {/* Agent Selection */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
            {Object.entries(AI_AGENTS).map(([agentId, agent]) => {
              const AgentIcon = agent.icon;
              const isSelected = selectedAgent === agentId;
              
              return (
                <Card
                  key={agentId}
                  className={cn(
                    "p-3 cursor-pointer transition-all duration-300 hover:scale-[1.02] border-0",
                    isSelected 
                      ? `bg-gradient-to-br ${agent.color} text-white shadow-lg shadow-primary/20` 
                      : "bg-card-bg hover:bg-card-hover border border-border/30"
                  )}
                  onClick={() => setSelectedAgent(agentId as keyof typeof AI_AGENTS)}
                >
                  <div className="flex flex-col items-center gap-2 text-center">
                    <AgentIcon className={cn(
                      "w-6 h-6 sm:w-8 sm:h-8",
                      isSelected ? "text-white" : "text-primary"
                    )} />
                    <div>
                      <div className="font-semibold text-xs sm:text-sm">{agent.name}</div>
                      <div className={cn(
                        "text-xs opacity-80",
                        isSelected ? "text-white/80" : "text-text-muted"
                      )}>
                        {agent.role}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Current Agent Info */}
      <div className="p-3 sm:p-4 bg-gradient-to-r from-card-bg to-card-hover border-b border-border/10">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${currentAgent.color}`}>
            <IconComponent className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-text-primary">{currentAgent.name}</h3>
            <p className="text-sm text-text-muted">{currentAgent.description}</p>
          </div>
        </div>
        
        {/* Expertise Tags */}
        <div className="flex flex-wrap gap-1 mt-3">
          {currentAgent.expertise.map((skill, index) => (
            <Badge 
              key={index} 
              variant="secondary" 
              className="text-xs bg-muted/50 text-text-muted"
            >
              {skill}
            </Badge>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
        {currentMessages.length === 0 && (
          <div className="space-y-6">
            <div className="text-center py-8">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${currentAgent.color} flex items-center justify-center`}>
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Hello! I'm {currentAgent.name}</h3>
              <p className="text-text-muted max-w-md mx-auto">
                {currentAgent.description}. How can I help you advance your career today?
              </p>
            </div>
            
            {/* Quick Prompts */}
            <QuickPrompts 
              selectedAgent={selectedAgent}
              onPromptSelect={(prompt) => sendMessage(prompt)}
              userContext={userContext}
            />
          </div>
        )}

        {currentMessages.map((message) => (
          <div
            key={message.id}
            className={cn(
              "flex gap-3 max-w-4xl",
              message.role === 'user' ? "ml-auto flex-row-reverse" : "mr-auto"
            )}
          >
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
              message.role === 'user' 
                ? "bg-primary text-white" 
                : `bg-gradient-to-br ${currentAgent.color}`
            )}>
              {message.role === 'user' ? (
                <User className="w-4 h-4" />
              ) : (
                <IconComponent className="w-4 h-4 text-white" />
              )}
            </div>
            
            <Card className={cn(
              "p-3 sm:p-4 max-w-[85%] border-0",
              message.role === 'user' 
                ? "bg-primary text-white" 
                : "bg-card-bg border border-border/30"
            )}>
              <div className="whitespace-pre-wrap break-words text-sm sm:text-base">
                {message.content}
              </div>
              <div className={cn(
                "text-xs mt-2 opacity-70",
                message.role === 'user' ? "text-white/70" : "text-text-muted"
              )}>
                {message.timestamp.toLocaleTimeString()}
              </div>
            </Card>
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3 mr-auto max-w-4xl">
            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${currentAgent.color} flex items-center justify-center flex-shrink-0`}>
              <IconComponent className="w-4 h-4 text-white" />
            </div>
            <Card className="p-4 bg-card-bg border border-border/30">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm text-text-muted">Thinking...</span>
              </div>
            </Card>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 sm:p-4 border-t border-border/20 bg-card-bg/50 backdrop-blur-sm">
        <div className="flex gap-2 sm:gap-3 max-w-4xl mx-auto">
          <div className="flex-1 relative">
            {isMobile ? (
              <Textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={`Ask ${currentAgent.name} anything...`}
                className="min-h-[44px] max-h-32 resize-none pr-12 bg-background border-border/30 focus:border-primary/50"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(inputMessage);
                  }
                }}
                disabled={isLoading}
              />
            ) : (
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={`Ask ${currentAgent.name} anything...`}
                className="pr-12 bg-background border-border/30 focus:border-primary/50 min-h-[44px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    sendMessage(inputMessage);
                  }
                }}
                disabled={isLoading}
              />
            )}
          </div>
          
          <Button
            onClick={() => sendMessage(inputMessage)}
            disabled={!inputMessage.trim() || isLoading}
            className={`min-h-[44px] min-w-[44px] bg-gradient-to-r ${currentAgent.color} hover:opacity-90 text-white shadow-lg`}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};